/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ActionKernelType, ActionType } from 'client-code/actions';
import { AiringOffer } from 'client-code/airing';
import { VideoResolution } from 'client-code/previews';
import { ViewType } from 'client-code/watch-stickiness';

import {
    APPLICATION_FEATURE_AREA,
    IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD,
    LAST_VIEWED_POSITION_INTERVAL_TIME,
    MILLISECONDS_IN_A_SECOND,
    SESSION_MANAGER_GRACE_PERIOD,
} from '../../config';
import { AppUtils } from '../AppUtils';
import { Constants } from '../constants';
import { DeviceModel } from '../device';
import { Session } from '../entities/Session';
import { ErrorTypes, LiveTvError } from '../error';
import { NdvrInteractor, ServicesClientImpl } from '../interactors';
import { Log, OperationsLogger, QoEAppLogger, VuLogger } from '../logging';
import { QoERegionModeType } from '../logging/qoe/QoERegionModeType';
import { PlaybackContentType } from '../playback/enums';
import { PlaybackDataModelImpl } from '../playback/impls';
import { Storage } from '../storage/Storage';
import { ITivoSubscription, TivoSubscriptionModel } from '../tivo';
import {
    TCollectionActivityStoreParams,
    TOfferPreviewCustomData,
    TOverlayOptions,
    TSocuLastViewedPositionParams,
} from '../types';
import { DateTime, GracePeriodUtils, TrackUtils } from '../utils';
import {
    MetadataState,
    MetadataType,
    PlaybackRestrictionType,
    PlayerViewMode,
    SessionState,
    VideoPlayerStates,
} from './enums';
import { LiveTvPlaybackErrorMapper } from './error';
import {
    IMetadata,
    IPlayable,
    IPlaybackError,
    IPlaybackIntervalInfo,
    IPlayerStats,
    IPlayerStatus,
    ISeekRange,
    ISessionCreateModel,
    ITrickplayControl,
    IVariantTrack,
} from './interfaces';
import { MetadataManager } from './MetadataManager';
import { RestrictionManager } from './RestrictionManager';
import { SessionManager } from './SessionManager';
import { VideoManager } from './VideoManager';

export class VideoPlaybackController {
    private static TAG: string = 'VideoPlaybackController';

    private static mVideoPlaybackController: VideoPlaybackController | null = null;

    private mReadySubcription = new TivoSubscriptionModel();

    // MetadataManager subscriptions
    private mMetadataStateSubcription: TivoSubscriptionModel<MetadataState> =
        new TivoSubscriptionModel<MetadataState>();

    // RestrictionManager subscriptions
    private mPlaybackRestrictionTypeSubcription: TivoSubscriptionModel<PlaybackRestrictionType> =
        new TivoSubscriptionModel<PlaybackRestrictionType>();

    // SessionManager subscriptions
    private mSessionStateSubcription: TivoSubscriptionModel<SessionState> =
        new TivoSubscriptionModel<SessionState>();

    // VideoManager subscriptions
    private mPlaybackErrorSubcription: TivoSubscriptionModel<any> =
        new TivoSubscriptionModel<any>();

    private mPlayerViewModeSubcription: TivoSubscriptionModel<PlayerViewMode> =
        new TivoSubscriptionModel<PlayerViewMode>();

    private mVideoPlayerStatesSubcription: TivoSubscriptionModel<IPlayerStatus> =
        new TivoSubscriptionModel<IPlayerStatus>();

    private mProgramBoundarySubscription: TivoSubscriptionModel<void> =
        new TivoSubscriptionModel<void>();

    private mPlayableChangeSubscription: ITivoSubscription<IPlayable> =
        new TivoSubscriptionModel<IPlayable>();

    // Managers declarations.
    private mMetadataManager: MetadataManager;

    private mRestrictionManager: RestrictionManager;

    private mSessionManager: SessionManager;

    private mVideoManager: VideoManager;

    // Other declarations.

    private mAudioTrackIds: number[];

    private mAudioTracksMap: Map<number, string>;

    private mErrorTime: any;

    private mGracePeriodTimerId: NodeJS.Timeout | null;

    private mIsAntiburnActive: boolean;

    private mIsGracePeriodFinished: boolean;

    private mIsForcedPaused: boolean;

    private mIsMetadataLoaded: boolean;

    private mIsNetworkConnected: boolean;

    private mIsPlayerLoaded: boolean;

    private mIsPlaying: boolean;

    private mIsReady: boolean;

    private mLVPIntervalId: NodeJS.Timeout | null;

    private mPlayable: IPlayable | null;

    private mPlayerError: LiveTvError | any;

    private mPlayerViewMode: PlayerViewMode;

    private mProgramBoundaryTimeoutId: NodeJS.Timeout | null;

    private mRestrictionType: PlaybackRestrictionType;

    private mSeekRangeStartTime: number | undefined;

    private mSelectedAudioTrack: string;

    private mSessionManagerGracePeriodTimeoutId!: NodeJS.Timeout;

    private mTextTracks: any[];

    private mUiContainerRef: HTMLDivElement | null;

    private mVideoEl: HTMLVideoElement | null;

    private constructor() {
        this.mAudioTrackIds = [];
        this.mAudioTracksMap = new Map();
        this.mIsAntiburnActive = false;
        this.mIsForcedPaused = false;
        this.mGracePeriodTimerId = null;
        this.mIsGracePeriodFinished = false;
        this.mErrorTime = null;
        this.mIsMetadataLoaded = false;
        this.mIsNetworkConnected = true;
        this.mIsPlayerLoaded = false;
        this.mIsPlaying = false;
        this.mIsReady = false;
        this.mLVPIntervalId = null;
        this.mMetadataManager = new MetadataManager();
        this.mPlayable = null;
        this.mPlayerError = null;
        this.mPlayerViewMode = PlayerViewMode.VideoWindow;
        this.mProgramBoundaryTimeoutId = null;
        this.mRestrictionManager = new RestrictionManager();
        this.mRestrictionType = PlaybackRestrictionType.NO_RESTRICTION;
        this.mSeekRangeStartTime = 0;
        this.mSelectedAudioTrack = '';
        this.mSessionManager = new SessionManager();
        this.mTextTracks = [];
        this.mUiContainerRef = null;
        this.mVideoEl = null;
        this.mVideoManager = new VideoManager();
    }

    public static getInstance(): VideoPlaybackController {
        if (VideoPlaybackController.mVideoPlaybackController === null) {
            VideoPlaybackController.mVideoPlaybackController = new VideoPlaybackController();
        }
        return VideoPlaybackController.mVideoPlaybackController;
    }

    // eslint-disable-next-line class-methods-use-this
    private initPlayer(): void {
        this.log('info', 'initPlayer');
        // Register Subscriptions
        this.mMetadataManager
            .getMetadataStateChangeListener()
            .subscribe(this.onMetadataStateChangeHandler.bind(this));
        this.mRestrictionManager
            .getRestrictionListener()
            .subscribe(this.onRestrictionHandler.bind(this));
        this.mSessionManager
            .getSessionStateChangeListener()
            .subscribe(this.onSessionStateChangeHandler.bind(this));
        this.mVideoManager
            .getPlaybackErrorListener()
            .subscribe(this.onPlaybackErrorHandler.bind(this));
        this.mVideoManager
            .getPlayerStateChangeListener()
            .subscribe(this.onPlayerStateChangeHandler.bind(this));
        this.mVideoManager
            .getPlayerViewChangeListener()
            .subscribe(this.onPlayerViewHandler.bind(this));
    }

    private isMetadataUpdateRequired(): boolean {
        const { airingOffers, offerPreview } = this.getMetadata();

        // Check for the cases in LIVE playback
        if (this.mPlayable?.playbackContentType === PlaybackContentType.LIVE) {
            // If airingOffers is not available or empty or for adult restriction,
            // metadata update is required
            if (
                !airingOffers ||
                !airingOffers.length ||
                this.mRestrictionType === PlaybackRestrictionType.ADULT
            )
                return true;

            // Check if the channel has changed
            if (airingOffers[0].stationId !== this.mPlayable?.channel?.stationId) return true;

            // Check if current time is greater than end time of current program
            const currentTime =
                this.mVideoManager!.getPlayerCurrentTime() * Constants.MILLISECOND_IN_ONE_SECOND ||
                new Date().getTime();
            const endTimeOfCurrentProgram =
                this.getEndTimeOfCurrentProgram(airingOffers, offerPreview).getTime() || 0;
            return currentTime > endTimeOfCurrentProgram;
        }
        // Check if playback content type is changed to SOCU OR NDVR
        return this.isSocu() || this.isNdvr();
    }

    private startInHomeGracePeriod(): void {
        this.mIsGracePeriodFinished = false;
        const consumedPeriod = GracePeriodUtils.getInstance().getConsumedGracePeriod();
        if (consumedPeriod < IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD) {
            if (!this.mGracePeriodTimerId) {
                const startTime = Date.now();
                this.mGracePeriodTimerId = setTimeout(() => {
                    GracePeriodUtils.getInstance().setConsumedGracePeriod(Date.now() - startTime);
                    this.mGracePeriodTimerId = null;
                    this.mIsGracePeriodFinished = true;
                    this.onRestrictionHandler(PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE);
                }, IN_HOME_LOCATION_PLAYBACK_GRACE_PERIOD);
            }
        } else {
            this.mIsGracePeriodFinished = true;
        }
    }

    private cleanPlayer(): void {
        this.getTrickplayControl().stopScrubbing();
        this.mAudioTrackIds = [];
        this.mAudioTracksMap.clear();
        this.mIsForcedPaused = false;
        this.mIsMetadataLoaded = false;
        this.mIsPlayerLoaded = false;
        this.mIsPlaying = false;
        this.mSessionManager.sessionStop();
        this.mTextTracks = [];
        clearTimeout(this.mSessionManagerGracePeriodTimeoutId);
        QoEAppLogger.updateAllHealthStatsMetrics(this.getPlayerStats());
        QoEAppLogger.updateProfileTime(this.getPlayerStats());
        QoEAppLogger.stopMediaHealthTimer();
        QoEAppLogger.logPlaybackStopped(
            this.getPlayerStats(),
            this.getPlayerCurrentTime(),
            this.getSeekRange(),
        );
    }

    private clearInHomeGracePeriodParameters(): void {
        const consumedPeriod = GracePeriodUtils.getInstance().getConsumedGracePeriod();
        this.mIsGracePeriodFinished = false;
        if (consumedPeriod) GracePeriodUtils.getInstance().setConsumedGracePeriod(0);
        if (this.mGracePeriodTimerId) {
            clearTimeout(this.mGracePeriodTimerId);
            this.mGracePeriodTimerId = null;
        }
    }

    private clearLastViewedPositionInterval(): void {
        if (this.mLVPIntervalId) {
            clearInterval(this.mLVPIntervalId);
            this.mLVPIntervalId = null;
        }
    }

    private getDisplayableLanguage(textTrack: any): string {
        const languageName = this.getLanguageByCode(textTrack.language);
        if (textTrack.kind === Constants.TEXT_TRACK_KIND_CAPTION) {
            const subLanguage = this.getSubLanguageByCode(
                textTrack.originalTextId,
                textTrack.mimeType,
            );
            return `${languageName} [${subLanguage}]`;
        }
        return languageName;
    }

    // eslint-disable-next-line class-methods-use-this
    private getLanguageByCode(code: string): string {
        if (!code) return Constants.ORIGINAL_LANGUAGE;
        return TrackUtils.getInstance().getLanguageByCode(code, Constants.UNKNOWN_LANGUAGE);
    }

    // eslint-disable-next-line class-methods-use-this
    private getSubLanguageByCode(originalTextId: string, mimeType: string): string {
        return TrackUtils.getInstance().getSubLanguageByCode(originalTextId, mimeType);
    }

    private getSelectedCCType(): string {
        return Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItemWithDefaultValue(
            Storage.Keys.PLAYER_CC_LANGUAGE_KIND,
            '',
        );
    }

    // eslint-disable-next-line class-methods-use-this
    private getSelectedCCLanguage(): string {
        return Storage.getInstance(Storage.StorageType.SESSION_STORAGE).getItemWithDefaultValue(
            Storage.Keys.PLAYER_CC_LANGUAGE,
            '',
        );
    }

    // eslint-disable-next-line class-methods-use-this
    private log(
        level: 'debug' | 'info' | 'error',
        message: string,
        ...optionalParams: any[]
    ): void {
        Diagnostics[level](VideoPlaybackController.TAG, message, ...optionalParams);
        Log[level[0]](VideoPlaybackController.TAG, message, ...optionalParams);
        if (level === 'error')
            Diagnostics.error(VideoPlaybackController.TAG, 'stacktrace:,', optionalParams[0]);
    }

    private getViewType(): ViewType {
        return this.getPlayable()?.playbackExtraArgs?.viewType ?? ViewType.Available;
    }

    private getCollectionActivityStoreParams(): TCollectionActivityStoreParams {
        const playable = this.getPlayable();
        if (this.isLive()) {
            const { airingOffers } = this.getMetadata();
            const { linearProviderPartnerId } = playable?.channel || {};
            const { banner = {}, logging = { contentId: '', collectionId: '' } } =
                airingOffers?.length ? airingOffers[0] : {};
            const { seasonNumber, movieYear } = banner;
            const { contentId, collectionId } = logging;

            return {
                contentId,
                seasonOrYear: seasonNumber ?? movieYear,
                partnerId: linearProviderPartnerId,
                collectionId,
                viewType: this.getViewType(),
            };
        }

        if (this.isNdvr()) {
            const watchAction = this.getWatchAction();
            const {
                collectionId,
                contentId,
                recordingProviderPartnerId,
                recordingId,
                seasonNumber,
                movieYear,
            } = watchAction?.kernel || {};

            return {
                contentId: contentId ?? '',
                collectionId: collectionId ?? '',
                partnerId: recordingProviderPartnerId,
                recordingId,
                seasonOrYear: seasonNumber ?? movieYear,
                viewType: this.getViewType(),
            };
        }
        // This is for SOCU
        const { watchAction, offerPreview } = this.mMetadataManager.getMetadata();
        const { seasonNumber, movieYear } = offerPreview?.contentPreview || {};
        const { collectionId, contentId, partnerId } = watchAction?.kernel || {};

        return {
            contentId: contentId ?? '',
            seasonOrYear: seasonNumber ?? movieYear,
            partnerId,
            collectionId: collectionId ?? '',
            viewType: this.getViewType(),
        };
    }

    private onAdultRestrictions(): void {
        if (!this.mIsForcedPaused && this.mIsPlayerLoaded) {
            this.mute(true);
            this.resume();
        } else if (
            this.mPlayable &&
            this.mPlayable.channel?.linearProviderPartnerId &&
            this.isLive()
        ) {
            this.mVideoManager?.setUpPlayer(
                this.mPlayable,
                this.mPlayable.channel?.linearProviderPartnerId,
            );
        }
    }

    private onInHomeError(): void {
        QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.ERROR);
        if (this.isPlaying()) this.stop();
        this.clearInHomeGracePeriodParameters();
    }

    private onInHomeUnreachable(): void {
        QoEAppLogger.updateConfigurationForRegionMode(QoERegionModeType.IN_GRACE_PERIOD);
        this.startInHomeGracePeriod();
        if (this.mIsGracePeriodFinished) {
            if (this.isPlaying()) this.stop();
        } else {
            this.resume();
        }
    }

    private onNetworkConnected(): void {
        this.mIsNetworkConnected = true;
        if (this.mIsAntiburnActive) return;

        if (!this.mIsForcedPaused) {
            this.restorePlayback();
        }
        if (
            this.mIsPlayerLoaded &&
            !this.mIsForcedPaused &&
            this.mPlayerViewMode === PlayerViewMode.VideoWindow
        )
            this.resume();
    }

    private onNetworkDisconnected(): void {
        this.mIsNetworkConnected = false;
        if (!this.mIsForcedPaused) this.pause();
    }

    private onNoRestrictions(): void {
        if (!this.mIsForcedPaused && this.mIsPlayerLoaded) {
            this.resume();
            this.mute(false);
        }
        this.clearInHomeGracePeriodParameters();
    }

    private onReadyHandler(): void {
        this.mReadySubcription.notify(true);
        this.log('debug', VideoPlaybackController.TAG, 'onReadyHandler');
    }

    private async onRecordingActionListReady() {
        const channelNumber =
            this.mMetadataManager
                .getMetadata()
                .actionList.actions.find((action) => action.kernel?.channelNumber)?.kernel
                ?.channelNumber ?? '';
        try {
            const channel = await new ServicesClientImpl().getChannelFromChannelNumber(
                channelNumber,
            );
            QoEAppLogger.logWatchContentStart(
                this.mPlayable?.playbackContentType,
                channel,
                [],
                this.getMetadata().offerPreview,
            );
        } catch (error) {
            this.log(
                'error',
                `onRecordingActionListReady - cannot find channel with channel number ${channelNumber}`,
                error,
            );
        }

        const restrictionType = this.mRestrictionManager.getRestrictionType();

        const isPlaybackAllowed =
            restrictionType === PlaybackRestrictionType.NO_RESTRICTION ||
            this.mRestrictionType === PlaybackRestrictionType.ADULT ||
            (!this.mIsGracePeriodFinished &&
                restrictionType === PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE);

        if (this.mPlayable && this.isNdvr() && this.getWatchAction() && isPlaybackAllowed) {
            this.mVideoManager?.setUpPlayer(
                this.mPlayable,
                this.getWatchAction().kernel?.recordingProviderPartnerId,
            );
        }
    }

    private onVODPreviewLoaded(): void {
        if (this.mPlayable) {
            const playbackRestrictionType = this.mRestrictionManager.checkAllRestriction(
                this.mPlayable,
                this.getMetadata(),
            );
            this.onRestrictionHandler(playbackRestrictionType);
        }
    }

    private onUnsupportedContent(subErrorCode: string, contentType: string): void {
        this.getPlaybackErrorListener().notify(
            new LiveTvPlaybackErrorMapper().getErrorByCode(
                ErrorTypes.LIVE_TV_UNSUPPORTED_CONTENT_ERROR_V950,
                subErrorCode,
                contentType,
            ),
        );
    }

    // ==== MetadataManager listeners ====
    private onMetadataStateChangeHandler(state: MetadataState): void {
        this.log('info', 'onMetadataStateChangeHandler', MetadataState[state]);
        const isLiveMetadata =
            state === MetadataState.AIRING_READY ||
            state === MetadataState.PREVIEW_READY ||
            state === MetadataState.PREVIEW_ERROR;
        if (isLiveMetadata) {
            this.scheduleProgramBoundary();
        }
        switch (state) {
            case MetadataState.AIRING_READY:
                QoEAppLogger.logWatchContentStart(
                    this.mPlayable?.playbackContentType,
                    this.getPlayable()?.channel,
                    this.getMetadata().airingOffers,
                );
                break;
            case MetadataState.AIRING_ERROR:
                this.clean();
                break;
            case MetadataState.PREVIEW_READY:
            case MetadataState.PREVIEW_ERROR:
                this.onMetadataLoaded();
                if (this.mIsPlayerLoaded) this.logStreamingEvents();
                break;
            case MetadataState.RECORDING_ACTION_READY:
                this.onMetadataLoaded();
                break;
            case MetadataState.RECORDING_ACTION_INIT_READY:
                this.onMetadataLoaded();
                this.onRecordingActionListReady();
                if (this.mIsPlayerLoaded) this.logStreamingEvents();
                break;
            case MetadataState.VOD_PREVIEW_READY:
                QoEAppLogger.logWatchContentStart(
                    this.mPlayable?.playbackContentType,
                    this.getPlayable()?.channel,
                    [],
                    this.getMetadata().offerPreview,
                );
                this.mIsMetadataLoaded = true;
                this.onVODPreviewLoaded();
                if (this.mIsPlayerLoaded) this.logStreamingEvents();
                break;
            case MetadataState.VOD_PREVIEW_ERROR:
                this.onVODPreviewLoaded();
                break;
            default:
        }
        this.mMetadataStateSubcription.notify(state);
    }

    public getWatchAction(): any {
        const watchAction = this.mMetadataManager
            .getMetadata()
            .actionList?.actions?.find((action) => {
                return (
                    action.actionType === ActionType.WatchNowAction &&
                    (action.kernel.actionKernelType ===
                        ActionKernelType.WatchFromMyShowsActionKernel ||
                        action.kernel.actionKernelType ===
                            ActionKernelType.ResumeFromMyShowsActionKernel)
                );
            });
        return watchAction;
    }

    private logStreamingEvents(): void {
        const shouldLogStreamingEvents =
            this.mRestrictionType === PlaybackRestrictionType.NO_RESTRICTION ||
            this.mRestrictionType === PlaybackRestrictionType.ADULT;
        if (shouldLogStreamingEvents) {
            QoEAppLogger.logStreamingStartEvent(this.mVideoManager.getSelectedAudioTrack());
            QoEAppLogger.logSessionCreatedEvent();
            QoEAppLogger.logVideoPlayerOpenStreamEvent();
        }
    }

    private onMetadataLoaded(): void {
        this.log('info', 'onMetadataLoaded');
        this.mIsMetadataLoaded = true;
        if (this.mPlayable) {
            const playbackRestrictionType = this.mRestrictionManager.checkAllRestriction(
                this.mPlayable,
                this.getMetadata(),
            );
            this.onRestrictionHandler(playbackRestrictionType);
            const { offerPreview } = this.getMetadata();
            const isUHDContent = offerPreview?.videoResolution === VideoResolution.Uhd;
            const isPPVContent = offerPreview?.isPpv;

            if (isPPVContent) {
                this.onUnsupportedContent('002', 'Pay-Per-View');
            } else if (isUHDContent) {
                this.onUnsupportedContent('001', 'UHD');
            }
        }
    }

    // ==== RestrictionManager listeners ====
    private onRestrictionHandler(type: PlaybackRestrictionType): void {
        this.mRestrictionType = type;
        this.mPlaybackRestrictionTypeSubcription.notify(type);
        this.log('info', 'onRestrictionHandler: ', PlaybackRestrictionType[type]);
        switch (type) {
            case PlaybackRestrictionType.ADULT:
                this.onAdultRestrictions();
                break;
            case PlaybackRestrictionType.BLACKLISTED:
            case PlaybackRestrictionType.OUT_OF_HOME:
            case PlaybackRestrictionType.OUT_OF_REGION:
            case PlaybackRestrictionType.UNSUBSCRIBED:
                this.stop();
                break;
            case PlaybackRestrictionType.INHOME_LOCATION_ERROR:
                this.onInHomeError();
                break;
            case PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE:
                this.onInHomeUnreachable();
                break;
            case PlaybackRestrictionType.NETWORK_CONNECTED:
                this.onNetworkConnected();
                break;
            case PlaybackRestrictionType.NETWORK_DISCONNECTED:
                this.onNetworkDisconnected();
                break;
            case PlaybackRestrictionType.NO_RESTRICTION:
                this.onNoRestrictions();
                break;
            default:
        }
    }

    // ==== SessionManager listeners ====
    private onSessionStateChangeHandler(state: SessionState): void {
        this.log('info', 'onSessionStateChangeHandler: ', SessionState[state]);
        clearTimeout(this.mSessionManagerGracePeriodTimeoutId);
        switch (state) {
            case SessionState.SUCCESS:
                this.onSessionSuccess();
                break;
            case SessionState.ERROR:
                this.onSessionError();
                break;
            case SessionState.UNREACHABLE:
                this.handleGracePeriod();
                break;
            default:
        }
        return this.mSessionStateSubcription.notify(state);
    }

    private handleGracePeriod(): void {
        this.mSessionManagerGracePeriodTimeoutId = setTimeout(() => {
            this.mSessionManager.setSessionError(ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718);
            this.mSessionManager.setSessionState(SessionState.ERROR);
            this.onSessionStateChangeHandler(SessionState.ERROR);
        }, SESSION_MANAGER_GRACE_PERIOD);
    }

    private onSessionError() {
        if (this.getSessionError().data) {
            VuLogger.logErrorEvent({
                code: this.getSessionError().data?.getCode() || '',
                message: this.getSessionError().data?.getMessage() || '',
                type: this.getSessionError().data?.getType() || '',
                source: VuLogger.ErrorEventSource.STREAMING_SESSION_ERROR,
            });
            VuLogger.logNotifyEvent(
                VuLogger.NotifyEventSourceType.ERROR_OVERLAY,
                VuLogger.NotifyEventSourceName.SESSION_ERROR,
            );
            QoEAppLogger.logSessionError(
                this.getSessionError().data || ({} as LiveTvError),
                this.getPlayerStats(),
                this.getPlayerCurrentTime(),
                this.getSeekRange(),
            );
            OperationsLogger.logUIErrorCode(this.getSessionError().data?.getCode() || '');
        }
        this.clean();
    }

    private onSessionSuccess() {
        const sessionPlaylistUrl = this.getSession().playlistUrl;
        if (sessionPlaylistUrl) {
            const httpsConvertedUri = this.mVideoManager?.getHttpsConvertedUrl(sessionPlaylistUrl);
            if (this.isStartOver()) {
                this.mVideoManager.overrideAvailabilityWindowForStartOver();
            }
            if (this.mVideoManager?.getStreamingUrl() !== httpsConvertedUri || !this.isLive()) {
                this.mVideoManager?.playUri(httpsConvertedUri);
            }
        }
    }

    // ==== VideoManager listeners ====

    private onDrmConfigured(): void {
        let sessionCreateModel: ISessionCreateModel;
        if (this.mPlayable?.playbackContentType === PlaybackContentType.LIVE) {
            if (!this.mPlayable?.channel) return;
            sessionCreateModel = {
                channel: this.mPlayable!.channel,
                transportEncodingType: this.mVideoManager!.getTransportEncodingType(),
                playbackContentType: this.mPlayable!.playbackContentType,
            };
            this.mSessionManager.createSession(sessionCreateModel);
            this.initPlayback();
        } else if (this.isNdvr()) {
            const watchAction = this.getWatchAction();
            sessionCreateModel = {
                transportEncodingType: this.mVideoManager!.getTransportEncodingType(),
                playbackContentType: this.mPlayable!.playbackContentType,
                recordingWatchAction: watchAction,
            };
            this.mSessionManager.createSession(sessionCreateModel);
        } else {
            if (!this.mPlayable?.channel) return;
            sessionCreateModel = {
                channel: this.mPlayable!.channel,
                transportEncodingType: this.mVideoManager!.getTransportEncodingType(),
                playbackContentType: this.mPlayable!.playbackContentType,
                watchAction: this.mMetadataManager.getMetadata().watchAction,
            };
            this.mSessionManager.createSession(sessionCreateModel);
        }
    }

    private onEndOfStream(): void {
        if (this.isNdvr()) {
            // Handled in VideoPlayerUI.tsx when we get the callback for End Of Stream
            this.updateLastViewedPosition();
            this.stop();
            return;
        }
        const actionDetails = this.getMetadata().watchAction;
        if (!actionDetails) {
            this.switchToLiveTv();
            return;
        }
        const startTime = new Date(actionDetails.startTime).getTime() / MILLISECONDS_IN_A_SECOND;
        const endOfSoCuContent = startTime + (actionDetails.kernel.duration ?? 0);
        const currentTime =
            this.getPlayerCurrentTime() ?? new Date().getTime() / MILLISECONDS_IN_A_SECOND;

        const isEndOfProgram = currentTime > endOfSoCuContent;

        if (currentTime + Constants.END_OF_THRESHOLD >= this.mVideoManager.getDuration()) {
            // This condition will be executed if player is ended with video tag ended property
            this.switchToLiveTv();
        } else if (
            this.isStartOver() &&
            !this.mVideoManager?.isDynamicManifest() &&
            !isEndOfProgram
        ) {
            // Old logic to handle start over functionality
            const playerCurrentTime = this.getPlayerCurrentTime();
            const lastViewedPosition = Math.round(
                playerCurrentTime - (this.mSeekRangeStartTime || 0),
            );
            this.mVideoManager?.restartPlaybackWithPosition(lastViewedPosition);
        } else {
            this.switchToLiveTv();
        }
    }

    private onFirstFrame(): void {
        QoEAppLogger.logFirstFrameEvent();
        QoEAppLogger.startMediaHealthTimer(
            this.getPlayerStats(),
            this.getPlayerCurrentTime(),
            this.getSeekRange(),
            this.mVideoManager.getSelectedAudioTrack(),
        );
        if (this.mPlayable?.playbackContentType === PlaybackContentType.LIVE) {
            this.mVideoManager.overrideAvailabilityWindowForLinear();
        }
        this.mSeekRangeStartTime = this.getSeekRange().start;
        this.updateWatchStickiness();
    }

    updateWatchStickiness() {
        try {
            new NdvrInteractor().setCollectionActivityStoreV2(
                this.getCollectionActivityStoreParams(),
            );
        } catch (error) {
            this.log('error', 'updateWatchStickiness ', error);
        }
    }

    private onPlaybackErrorHandler(error: LiveTvError): void {
        this.log('info', 'onPlaybackErrorHandler: ', error);
        VuLogger.logErrorEvent({
            code: error.getCode(),
            message: error.getMessage(),
            source: VuLogger.ErrorEventSource.STREAMING_SESSION_ERROR,
            type: error.getType(),
        });
        OperationsLogger.logUIErrorCode(error.getCode());
        VuLogger.logNotifyEvent(
            VuLogger.NotifyEventSourceType.ERROR_OVERLAY,
            VuLogger.NotifyEventSourceName.PLAYER_ERROR,
        );
        this.mPlayerError = error;
        QoEAppLogger.logPlaybackError(
            error,
            this.getPlayerStats(),
            this.getPlayerCurrentTime(),
            this.getSeekRange(),
        );
        this.mPlaybackErrorSubcription.notify(error);
    }

    private onPlayerStateLoaded(): void {
        this.mIsPlayerLoaded = true;
        if (this.mIsMetadataLoaded) {
            this.logStreamingEvents();
        }
        this.updateSelectedAudioTrack();
        if (this.mIsMetadataLoaded || this.isSocu()) {
            this.resume();
        }
        QoEAppLogger.stopMediaHealthTimer();
    }

    private onPlayerStatePlaying(eventTimeStamp: number | undefined): void {
        this.mIsPlaying = true;
        if (!this.isLive()) {
            this.setLastViewedPositionInterval();
            QoEAppLogger.updateHealthStatsMetricsByEvent({
                type: QoEAppLogger.PlayerEvents.PLAY,
                timeStamp: eventTimeStamp,
            });
        } else
            DeviceModel.getAppSettings().setLastTunedChannelNumber(
                this.mPlayable?.channel?.channelNumber!,
            );
    }

    private onPlayerStateUnLoaded(): void {
        this.mIsPlaying = false;
        this.mIsPlayerLoaded = false;
        this.clearLastViewedPositionInterval();
    }

    private onPlayerStateChangeHandler(status: IPlayerStatus): void {
        this.mVideoPlayerStatesSubcription.notify(status);
        this.log('debug', 'The video player state changed to', VideoPlayerStates[status.state]);
        switch (status.state) {
            case VideoPlayerStates.ADAPTATION:
                this.onPlayerStateAdaptation(status);
                break;
            case VideoPlayerStates.DRM_CONFIGURED:
                this.onDrmConfigured();
                break;
            case VideoPlayerStates.END_OF_STREAM:
                this.onEndOfStream();
                break;
            case VideoPlayerStates.FIRST_FRAME:
                this.onFirstFrame();
                break;
            case VideoPlayerStates.LOADED:
                this.onPlayerStateLoaded();
                break;
            case VideoPlayerStates.MANIFEST_PARSED:
                QoEAppLogger.updateHealthStatsMetricsByEvent({
                    type: QoEAppLogger.PlayerEvents.BUFFERING,
                    timeStamp: status.event?.eventTimestamp,
                });
                break;
            case VideoPlayerStates.PAUSE:
                this.onPlayerStatePause(status);
                break;
            case VideoPlayerStates.PLAYING:
                this.onPlayerStatePlaying(status.event?.eventTimestamp);
                break;
            case VideoPlayerStates.STALLED:
                QoEAppLogger.updateHealthStatsMetricsByEvent({
                    type: QoEAppLogger.PlayerEvents.STALLED,
                    timeStamp: status.event?.eventTimestamp,
                });
                break;
            case VideoPlayerStates.UNLOADED:
                this.onPlayerStateUnLoaded();
                break;
            case VideoPlayerStates.INITIATION:
                this.onPlayerInitiation();
                break;
            default:
        }
    }

    private onPlayerStateAdaptation(status: IPlayerStatus): void {
        const oldTrack = status.event?.oldTrack;
        if (!QoEAppLogger.profiles.length) {
            QoEAppLogger.createProfiles(this.mVideoManager.getVideoVariants());
        }
        if (!QoEAppLogger.isAdaptationEventFirstTime && !oldTrack) {
            QoEAppLogger.isAdaptationEventFirstTime = true;
            const time = Date.now() - QoEAppLogger.watchContentStartTime;
            QoEAppLogger.updateProfileTime(this.getPlayerStats(), time);
        } else if (oldTrack) {
            const intervalTime =
                QoEAppLogger.mediaHealthStartIntervalTime || QoEAppLogger.watchContentStartTime;
            const time = Date.now() - intervalTime;
            QoEAppLogger.updateProfileTime(oldTrack, time);
            QoEAppLogger.mediaStats.profileShiftCount += 1;
            QoEAppLogger.mediaHealthStartIntervalTime = Date.now();
        }
    }

    private onPlayerInitiation(): void {
        VuLogger.playUrl = this.mVideoManager.getStreamingUrl();
        QoEAppLogger.updateStreamingUrl(this.mVideoManager.getStreamingUrl());
        this.mPlayerError = null;
    }

    private onPlayerStatePause(status: IPlayerStatus): void {
        this.clearLastViewedPositionInterval();
        QoEAppLogger.updateHealthStatsMetricsByEvent({
            type: QoEAppLogger.PlayerEvents.PAUSE,
            timeStamp: status.event?.eventTimestamp,
        });
    }

    private onPlayerViewHandler(mode: PlayerViewMode): void {
        this.log('info', 'onPlayerViewHandler: ', PlayerViewMode[mode]);
        this.mPlayerViewMode = mode;
        this.mPlayerViewModeSubcription.notify(mode);
    }

    private prepareMetadataForPlayable(playable: IPlayable) {
        if (this.isMetadataUpdateRequired() && !this.isNdvr()) {
            this.mMetadataManager.prepareMetadata(this.mPlayable!);
        } else {
            QoEAppLogger.logWatchContentStart(
                this.mPlayable?.playbackContentType,
                playable.channel,
                this.getMetadata().airingOffers,
            );
            this.onMetadataLoaded();
        }
    }

    // ===== UI Listeners =====
    public getMetadataStateChangeListener(): ITivoSubscription<MetadataState> {
        return this.mMetadataStateSubcription;
    }

    public getPlaybackErrorListener(): ITivoSubscription<any> {
        return this.mPlaybackErrorSubcription;
    }

    public getPlayerViewListener(): ITivoSubscription<PlayerViewMode> {
        return this.mPlayerViewModeSubcription;
    }

    public getReadyListener() {
        return this.mReadySubcription;
    }

    public getRestrictionListener(): ITivoSubscription<PlaybackRestrictionType> {
        return this.mPlaybackRestrictionTypeSubcription;
    }

    public getSessionStateChangeListener(): ITivoSubscription<SessionState> {
        return this.mSessionStateSubcription;
    }

    public getPlayerStateChangeListener(): ITivoSubscription<IPlayerStatus> {
        return this.mVideoPlayerStatesSubcription;
    }

    public getPlayableChangeListener(): ITivoSubscription<IPlayable> {
        return this.mPlayableChangeSubscription;
    }

    public getProgramBoundaryListener(): ITivoSubscription<void> {
        return this.mProgramBoundarySubscription;
    }

    private play(playable: IPlayable) {
        this.log('info', 'play');
        let playbackRestrictionType;
        if (this.isMetadataUpdateRequired()) {
            this.mMetadataManager?.prepareMetadata(playable);
            playbackRestrictionType = this.checkCanPlayStartRestriction(playable);
        } else {
            this.mIsMetadataLoaded = true;
            playbackRestrictionType = this.checkAllRestriction(this.mPlayable!, this.getMetadata());
            QoEAppLogger.logWatchContentStart(
                this.mPlayable?.playbackContentType,
                playable.channel,
                this.getMetadata()?.airingOffers,
            );
        }
        this.onRestrictionHandler(playbackRestrictionType);
        if (
            playbackRestrictionType === PlaybackRestrictionType.NO_RESTRICTION ||
            (!this.mIsGracePeriodFinished &&
                playbackRestrictionType === PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE)
        )
            if (!this.isNdvr() && playable.channel?.linearProviderPartnerId)
                this.mVideoManager?.setUpPlayer(
                    playable,
                    playable.channel?.linearProviderPartnerId,
                );
            else this.clean();
    }

    /** -------Audio Control------- */

    private updateSelectedAudioTrack(): void {
        this.mSelectedAudioTrack =
            this.getAudioTracks().filter((track) => track.isSelected)[0]?.text || '';
    }

    public getAudioTracks(): TOverlayOptions[] {
        const audioTracks = this.mVideoManager.getAudioTracks();

        if (this.mAudioTracksMap.size === 0) {
            this.mAudioTracksMap = TrackUtils.getInstance().getAudioTrackMap(audioTracks);
        }
        const currTrack = this.mVideoManager.getSelectedAudioTrack();
        const currId = currTrack !== undefined ? currTrack.audioId : -1;

        const audioTracksForOverlay: any[] = [];
        this.mAudioTrackIds = [];
        if (this.mAudioTracksMap.size > 1) {
            // track a[key, value] => key is audioId and value is language string
            const mapToArray = [...Array.from(this.mAudioTracksMap)];
            this.mAudioTracksMap = new Map(mapToArray.sort((a, b) => a[1].localeCompare(b[1])));
        }
        this.mAudioTracksMap.forEach((value, key) => {
            if (key === currId) {
                this.mAudioTrackIds.unshift(key);
                audioTracksForOverlay.unshift({
                    text: value,
                    hasSelectMark: true,
                    isSelected: true,
                });
            } else {
                this.mAudioTrackIds.push(key);
                audioTracksForOverlay.push({
                    text: value,
                    hasSelectMark: true,
                    isSelected: false,
                });
            }
        });
        this.log('debug', 'getAudioTracks:', audioTracksForOverlay);
        return audioTracksForOverlay;
    }

    public getSelectedAudioTrack(): string {
        return this.mSelectedAudioTrack;
    }

    public mute(muteAudio: boolean): void {
        this.mVideoManager?.mute(muteAudio);
    }

    public setAudioTrackByLanguage(language: string): void {
        this.mVideoManager?.setAudioTrackByLanguage(language);
    }

    public setAudioTrack(selectedIndex: number): void {
        if (this.getAudioTracks().length > selectedIndex) {
            const audioId = this.mAudioTrackIds[selectedIndex];
            this.log('info', 'setSelectedAudioTrack', audioId);
            this.setAudioTrackByID(audioId);
            this.updateSelectedAudioTrack();
            VuLogger.logUserActionEvent({
                actionName: VuLogger.UserActionEventActionName.AUDIO_LANGUAGE,
                action: VuLogger.UserActionEventAction.UPDATE,
                screen: VuLogger.UserActionEventScreen.OVERLAYS,
                selection: this.getSelectedAudioTrack(),
            });
        }
    }

    private setAudioTrackByID(audioID: number): number {
        const currentAudioTrack = this.mVideoManager.getSelectedAudioTrack();
        if (this.mVideoManager.getSelectedAudioTrack()) {
            if (audioID === currentAudioTrack?.audioId) {
                this.log('info', 'setAudioTrackByID', `Already playing {audioId: `, audioID, '}');
                return -1;
            }
        }
        if (this.mVideoManager.getAudioTracks().length > 0) {
            const audioTracks = this.mVideoManager.getAudioTracks();
            const track = audioTracks.find(({ audioId }) => audioId === audioID);
            const variants = this.getVideoVariants();
            const sameBitrateTrack = variants.find(
                ({ language, videoBandwidth, audioId }) =>
                    language === track?.language &&
                    videoBandwidth === currentAudioTrack?.videoBandwidth &&
                    audioId === track?.audioId,
            );
            const selectedTrack = sameBitrateTrack ?? track;
            if (selectedTrack) {
                this.mVideoManager.setAudioTrack(selectedTrack);
                QoEAppLogger.logAudioTrackChanged(
                    this.getPlayerStats(),
                    this.getPlayerCurrentTime(),
                    this.getSeekRange(),
                );
                QoEAppLogger.logStreamingStartEvent(track);
                return selectedTrack.id;
            }
            this.log('error', 'setAudioTrackByID ', track);
        }
        return -1;
    }

    // ==== Closed Captions Control ====
    public isClosedCaptionActive(): boolean {
        return this.mVideoManager?.isClosedCaptionActive()!;
    }

    public toggleClosedCaption(): void {
        this.mVideoManager?.toggleClosedCaption();
    }

    /** ------- NDVR ------- */

    public isNdvr(): boolean {
        return (
            this.mPlayable?.playbackContentType === PlaybackContentType.NDVR_LIVE ||
            this.mPlayable?.playbackContentType === PlaybackContentType.NDVR_RECORDED
        );
    }

    public isNdvrLive(): boolean {
        return this.isNdvr() && this.isRecordingInProgress();
    }

    public isNdvrRecorded(): boolean {
        return this.isNdvr() && !this.isRecordingInProgress();
    }

    public isRecordingInProgress(): boolean {
        let isRecordingInProgress: boolean = false;
        if (
            !AppUtils.isEmpty(this.getWatchAction()) &&
            this.getWatchAction().kernel.isRecordingInProgress
        ) {
            isRecordingInProgress = this.getWatchAction().kernel.isRecordingInProgress;
        }
        return isRecordingInProgress;
    }

    private updateNdvrLastViewedPosition(): void {
        const recordingId = this.getWatchAction()?.kernel.recordingId || '';
        const currentTime = this.getPlayerCurrentTime();
        let lastViewedPosition = Math.round(currentTime);
        if (this.isNdvrLive()) {
            lastViewedPosition = Math.round(currentTime - this.getSeekRange().start);
        }
        try {
            new NdvrInteractor()
                .getRequestModelToUpdateRecordingsLastViewedPosition(
                    recordingId,
                    lastViewedPosition,
                )
                .send();
        } catch (error) {
            Log.e(VideoPlaybackController.TAG, 'error', error);
            throw error;
        }
    }

    /** -------Playback Control------- */
    public pause(forced: boolean = false): void {
        this.mIsPlaying = false;
        this.mVideoManager?.pause();
        this.mIsForcedPaused = forced || this.mIsForcedPaused;
    }

    /**
     * Triggers playback from the controller on URL change from session or source.
     * @param {string} uri
     */
    public playUri(uri: string): void {
        this.mVideoManager?.playUri(uri);
    }

    public resume(): void {
        this.mIsPlaying = true;
        this.mVideoManager?.resume();
        this.mIsForcedPaused = false;
    }

    public async stop(): Promise<void> {
        this.cleanPlayer();
        await this.mVideoManager?.stop();
    }

    public async switchToLiveTv(): Promise<void> {
        this.log('debug', 'switchToLiveTv: ', this.mPlayable?.channel?.channelNumber);
        PlaybackDataModelImpl.getInstance().resetPlaybackData();
        this.updateLastViewedPosition();

        if (this.isSocu() && this.mPlayable && this.mPlayable.channel) {
            VideoPlaybackController.getInstance().startPlayback({
                channel: this.mPlayable.channel,
                playbackContentType: PlaybackContentType.LIVE,
            });
            return;
        }

        const playable =
            await new ServicesClientImpl().getLastPlayedOrFallbackToFirstByChannelNumber(
                DeviceModel.getAppSettings().getLastTunedChannelNumber(),
            );

        VideoPlaybackController.getInstance().startPlayback(playable);
    }

    public getCurrentChannelNumber(): string {
        let channelNumber = '';
        if (this.mPlayable) {
            channelNumber =
                this.mPlayable.channel?.channelNumber ||
                DeviceModel.getAppSettings().getLastTunedChannelNumber() ||
                '';
        }
        return channelNumber;
    }

    // ==== Playback State ====

    public setUpPlayer(playable: IPlayable, partnerId: string): void {
        this.mVideoManager?.setUpPlayer(playable, partnerId);
    }

    public clean(): void {
        this.cleanPlayer();
        this.mVideoManager.clean();
    }

    public setPlayPosition(position: number): void {
        this.mVideoManager?.setPlayPosition(position);
    }

    // ==== Player Metadata ====

    public getAudioVideoCodec(): string {
        return this.mVideoManager?.getAudioVideoCodec()!;
    }

    public getDrmServerType(): string {
        return this.mVideoManager?.getDrmServerType()!;
    }

    public getPlayerCurrentTime(): number {
        return this.mVideoManager?.getPlayerCurrentTime()!;
    }

    public getPlayerStats(): IPlayerStats {
        return this.mVideoManager?.getPlayerStats()!;
    }

    public getSeekRange(): ISeekRange {
        return this.mVideoManager?.getSeekRange()!;
    }

    public getStreamingUrl(): string {
        return this.mVideoManager.getStreamingUrl();
    }

    public getTransportEncodingType(): string {
        this.log(
            'debug',
            'getTransportEncodingType: ',
            this.mVideoManager?.getTransportEncodingType()[0].toString(),
        );
        return this.mVideoManager?.getTransportEncodingType()[0].toString();
    }

    public getVideoVariants(): IVariantTrack[] {
        return this.mVideoManager?.getVideoVariants()!;
    }

    // ==== LIVE ==== //

    public isLive(): boolean {
        return this.mPlayable?.playbackContentType === PlaybackContentType.LIVE;
    }

    public isLiveWithRecording(): boolean {
        let hasRecording: boolean = false;
        if (!AppUtils.isEmpty(this.getWatchAction()) && this.getWatchAction().hasRecording) {
            hasRecording = this.getWatchAction().hasRecording;
        }
        return this.mPlayable?.playbackContentType === PlaybackContentType.LIVE && hasRecording;
    }

    // ==== SOCU ==== //

    public isCatchUp(): boolean {
        return (
            this.mPlayable?.playbackContentType === PlaybackContentType.CATCHUP_BEGINNING ||
            this.mPlayable?.playbackContentType === PlaybackContentType.CATCHUP_RESUME
        );
    }

    public isSocu(): boolean {
        return this.isStartOver() || this.isCatchUp();
    }

    public isStartOver(): boolean {
        return (
            this.mPlayable?.playbackContentType === PlaybackContentType.STARTOVER_BEGINNING ||
            this.mPlayable?.playbackContentType === PlaybackContentType.STARTOVER_RESUME
        );
    }

    private updateSocuLastViewedPosition(currentTime, screenType): void {
        try {
            const { collectionId, partnerOfferId, partnerId, contentId, duration } =
                this.getMetadata()?.watchAction?.kernel || {};
            if (currentTime && contentId && partnerOfferId) {
                let lastViewedPosition = Math.round(currentTime);
                if (
                    this.mPlayable?.playbackContentType ===
                        PlaybackContentType.STARTOVER_BEGINNING ||
                    this.mPlayable?.playbackContentType === PlaybackContentType.STARTOVER_RESUME
                ) {
                    lastViewedPosition = Math.round(currentTime - this.getSeekRange().start);
                }
                const socuLVPParams: TSocuLastViewedPositionParams = {
                    lastViewedPosition,
                    partnerOfferId,
                    providerPartnerId: partnerId || '',
                    screenType,
                    contentId,
                    collectionId,
                    duration,
                };
                new ServicesClientImpl().setSocuLastViewedPosition(socuLVPParams);
            }
        } catch (error) {
            Log.e(VideoPlaybackController.TAG, 'error', error);
            throw error;
        }
    }

    // ==== Text Track Control ====
    public getSelectedTextTrack(): string {
        const activeSubtitleTrack = this.mVideoManager
            .getTextTracks()
            .find(({ active }) => active === true);
        if (activeSubtitleTrack) {
            return this.getDisplayableLanguage(activeSubtitleTrack);
        }

        const selectedLanguage = this.getSelectedCCLanguage();
        const selectedKind = this.getSelectedCCType();
        const subtitleTrack =
            this.mVideoManager
                .getTextTracks()
                .find(
                    ({ language, kind }) => language === selectedLanguage && kind === selectedKind,
                ) ||
            this.mVideoManager
                .getTextTracks()
                .find(({ language }) => language === selectedLanguage) ||
            this.mVideoManager.getTextTracks()[0];
        if (subtitleTrack) {
            return this.getDisplayableLanguage(subtitleTrack);
        }

        return '';
    }

    /** This will return list of subtitles & selecetd will be on top */
    public getTextTracks(): TOverlayOptions[] {
        const selectedTrackID = this.getSelectedTextTrackID();
        this.mTextTracks = this.mVideoManager.getTextTracks().sort((trackItem1, trackItem2) => {
            return (
                Number(trackItem2.id === selectedTrackID) -
                Number(trackItem1.id === selectedTrackID)
            );
        });
        const textTracksForOverlay: any[] = [];
        this.mTextTracks.forEach((textTrack, index) => {
            textTracksForOverlay.push({
                text: this.getDisplayableLanguage(textTrack),
                hasSelectMark: true,
                isSelected: index === 0,
            });
        });
        return textTracksForOverlay;
    }

    public getSelectedTextTrackID(): number {
        const activeSubtitleTrack = this.mVideoManager
            .getTextTracks()
            .find(({ active }) => active === true);
        if (activeSubtitleTrack) {
            return activeSubtitleTrack.id;
        }

        const selectedLanguage = this.getSelectedCCLanguage();
        const selectedKind = this.getSelectedCCType();
        const subtitleTrack =
            this.mVideoManager
                .getTextTracks()
                .find(
                    ({ language, kind }) => language === selectedLanguage && kind === selectedKind,
                ) ||
            this.mVideoManager
                .getTextTracks()
                .find(({ language }) => language === selectedLanguage) ||
            this.mVideoManager.getTextTracks()[0];
        if (subtitleTrack) {
            return subtitleTrack.id;
        }

        return -1;
    }

    public isTextTrackChangeSupported(): boolean {
        return this.mVideoManager.isTextTrackChangeSupported();
    }

    public isTextTrackSupported(): boolean {
        return this.mVideoManager?.isTextTrackSupported()!;
    }

    public setTextTrackByIndex(selecetdIndex: number): void {
        const selectedTrack = this.mTextTracks[selecetdIndex];
        const selectedLanguage = selectedTrack.language;
        const selectedKind = selectedTrack.kind;
        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
            Storage.Keys.PLAYER_CC_LANGUAGE,
            selectedLanguage,
        );
        Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
            Storage.Keys.PLAYER_CC_LANGUAGE_KIND,
            selectedKind,
        );
        this.mVideoManager?.setTextTrack(selectedTrack);
        VuLogger.logUserActionEvent({
            actionName: VuLogger.UserActionEventActionName.SUBTITLE_AND_CC,
            action: VuLogger.UserActionEventAction.UPDATE,
            screen: VuLogger.UserActionEventScreen.OVERLAYS,
            selection: selectedLanguage,
        });
    }

    // ==== Trickplay Control ====

    public getTrickplayControl(): ITrickplayControl {
        return this.mVideoManager.getTrickplayControl();
    }

    public isTrickplayEnabled(): boolean {
        return this.mVideoManager.isTrickplayEnabled();
    }

    public trickModeRestrictions() {
        return this.getSession().trickModeRestrictions;
    }

    // ==== View Control ====

    public getPlayerViewMode(): string {
        return this.mPlayerViewMode;
    }

    public maximize(): void {
        this.mVideoManager?.maximize();
    }

    public minimize(): void {
        this.mVideoManager?.minimize();
    }

    // ==== MetadataManger public functions ====

    public getMetadata(): IMetadata {
        return this.mMetadataManager.getMetadata();
    }

    public getMetadataError(): LiveTvError {
        return this.mMetadataManager.getMetadataError();
    }

    public getMetadataState(): MetadataState {
        return this.mMetadataManager.getMetadataState();
    }

    public prepareMetadata(playable: IPlayable): void {
        this.log('info', 'prepareMetadata ', playable);
        this.mMetadataManager.prepareMetadata(playable);
    }

    public prepareMetadataByType(
        playable: IPlayable,
        metadataType: MetadataType,
        playerCurrentTime?: number,
    ): void {
        this.mMetadataManager.prepareMetadataByType(playable, metadataType, playerCurrentTime);
    }

    // ==== RestrictionManager public functions ====

    public checkAllRestriction(playable: IPlayable, metadata: IMetadata): PlaybackRestrictionType {
        return this.mRestrictionManager?.checkAllRestriction(playable, metadata);
    }

    public checkCanPlayStartRestriction(playable: IPlayable): PlaybackRestrictionType {
        return this.mRestrictionManager?.checkCanPlayStartRestriction(playable)!;
    }

    public getRestrictionType(): PlaybackRestrictionType {
        return this.mRestrictionManager.getRestrictionType();
    }

    // ==== SessionManager public functions ====

    public getPlayerError() {
        return this.mPlayerError;
    }

    public getSession(): Session {
        return this.mSessionManager.getSession();
    }

    public getSessionError(): IPlaybackError {
        return this.mSessionManager.getSessionError();
    }

    public getSessionState(): SessionState {
        return this.mSessionManager.getSessionState();
    }

    // ==== VideoManager public functions ====

    public initPlayback(): void {
        this.mVideoManager.initPlayback();
    }

    public mount(): void {
        this.mVideoManager.mount();
    }

    public unmount(): void {
        this.clean();
        this.mVideoManager.unmount();
    }

    // ==== Other public functions ====

    public configurePlayer(videoEl: HTMLVideoElement, uiContainerRef: HTMLDivElement): void {
        this.log('info', 'configurePlayer');
        this.mVideoEl = videoEl;
        this.mUiContainerRef = uiContainerRef;
        this.mVideoManager.init(videoEl, uiContainerRef);
        this.initPlayer();
    }

    public getPlayable(): IPlayable | null {
        return this.mPlayable;
    }

    public getPlaybackIntervalInfo(): IPlaybackIntervalInfo {
        return {
            currentTime: this.getPlayerCurrentTime(),
            seekRange: this.getSeekRange(),
        };
    }

    public isForcePausedByUser(): boolean {
        return this.mIsForcedPaused;
    }

    public isGracePeriodFinished(): boolean {
        return this.mIsGracePeriodFinished;
    }

    public isNetworkConnected(): boolean {
        return this.mIsNetworkConnected;
    }

    public isPlaybackStarted(): boolean {
        return !!this.getPlayerStats()?.playTime;
    }

    public isPlayerLoaded(): boolean {
        return this.mIsPlayerLoaded;
    }

    public isPlaying(): boolean {
        this.log('info', 'isPlaying ', this.mIsPlaying);
        return this.mIsPlaying;
    }

    public ready(): void {
        this.log('info', 'ready ', this.mIsReady);
        if (this.mIsReady) return;
        this.mIsReady = true;
        if (DeviceModel.getAppSettings().isVideoWindowEnabled()) this.mount();
        if (!this.mIsPlaying && this.mPlayable) {
            this.mIsPlaying = true;
            this.play(this.mPlayable!);
        }
        this.onReadyHandler();
    }

    public restorePlayback(): void {
        const isVideoWindowDisabled = !DeviceModel.getAppSettings().isVideoWindowEnabled();
        const isPlayingInFullScreen = this.mPlayerViewMode === PlayerViewMode.FullScreen;
        this.mPlayerError = null;
        if (this.mPlayable?.channel === null || (isVideoWindowDisabled && !isPlayingInFullScreen))
            return;
        const playbackRestrictionType = this.mRestrictionManager.checkCanPlayStartRestriction(
            this.mPlayable!,
        );
        if (
            playbackRestrictionType === PlaybackRestrictionType.NO_RESTRICTION ||
            (!this.mIsGracePeriodFinished &&
                playbackRestrictionType === PlaybackRestrictionType.INHOME_LOCATION_UNREACHABLE)
        ) {
            if (this.mPlayable) {
                if (this.mIsPlayerLoaded) this.prepareMetadataForPlayable(this.mPlayable);
                else this.startPlayback(this.mPlayable, false);
            }
        } else {
            this.onRestrictionHandler(playbackRestrictionType);
            Log.i(VideoPlaybackController.TAG, 'Banned or Same channel recieved ');
        }
    }

    public updateCurrentPlayPosition(): void {
        switch (this.mPlayable?.playbackContentType) {
            case PlaybackContentType.CATCHUP_RESUME:
                this.setPlayPosition(this.getMetadata()?.watchAction?.kernel?.savedPosition || 0);
                break;

            case PlaybackContentType.STARTOVER_BEGINNING:
                this.setPlayPosition(this.getSeekRange().start - this.getPlayerCurrentTime());
                break;

            case PlaybackContentType.STARTOVER_RESUME:
                this.mVideoManager?.setPlayPosition(
                    this.getSeekRange().start +
                        (this.getMetadata()?.watchAction?.kernel?.savedPosition || 0) -
                        this.getPlayerCurrentTime(),
                );
                break;

            case PlaybackContentType.LIVE:
                this.getTrickplayControl().goToLive();
                break;

            case PlaybackContentType.NDVR_LIVE:
                if (!this.mPlayable?.ndvrPlaybackModel?.isStartOver)
                    this.setPlayPosition(
                        this.getSeekRange().start +
                            (this.getMetadata()?.ndvrLastViewedPosition ?? 0) -
                            this.getPlayerCurrentTime(),
                    );
                break;
            case PlaybackContentType.NDVR_RECORDED:
                if (!this.mPlayable?.ndvrPlaybackModel?.isStartOver)
                    this.setPlayPosition(this.getMetadata()?.ndvrLastViewedPosition ?? 0);
                break;

            default:
        }
    }

    private async startLivePlayback(playable: IPlayable, isForcedPlay: boolean): Promise<void> {
        if (
            (!this.mIsPlaying && !this.mIsForcedPaused) ||
            this.mPlayable?.channel?.channelNumber !== playable.channel?.channelNumber ||
            this.mPlayable?.playbackContentType !== playable?.playbackContentType ||
            this.mRestrictionType === PlaybackRestrictionType.ADULT
        ) {
            if (this.mIsPlaying) await this.stop();
            this.setPlayable(playable);
            this.play(playable);
        } else if (isForcedPlay) {
            this.resume();
        }
    }

    private async startSoCuPlayback(playable: IPlayable): Promise<void> {
        if (this.shouldSkipSoCuPlaybackRestart(playable)) {
            if (this.mIsForcedPaused) {
                this.resume();
            }
            this.setPlayable(playable);
            return;
        }
        if (this.mIsPlaying) {
            await this.stop();
        }
        this.setPlayable(playable);
        this.play(playable);
    }

    private async startNdvrPlayback(playable: IPlayable): Promise<void> {
        if (
            playable.ndvrPlaybackModel?.isStartOver ||
            !this.mIsPlaying ||
            this.mPlayable?.ndvrPlaybackModel?.id !== playable.ndvrPlaybackModel?.id
        ) {
            if (this.mIsPlaying) {
                await this.stop();
            }
            this.setPlayable(playable);
            this.play(playable);
        } else if (this.mIsPlaying && this.mRestrictionType === PlaybackRestrictionType.ADULT) {
            const playbackRestrictionType = this.checkCanPlayStartRestriction(playable);
            this.onRestrictionHandler(playbackRestrictionType);
        }
    }

    public async startPlayback(playable: IPlayable, isForcedPlay: boolean = true): Promise<void> {
        this.log('info', 'startPlayback ', playable);
        if (!this.mIsReady) {
            this.setPlayable(playable);
            return;
        }
        if (!this.mIsNetworkConnected) {
            this.onRestrictionHandler(PlaybackRestrictionType.NETWORK_DISCONNECTED);
            return;
        }
        switch (playable.playbackContentType) {
            case PlaybackContentType.LIVE:
                this.startLivePlayback(playable, isForcedPlay);

                break;
            case PlaybackContentType.STARTOVER_BEGINNING:
            case PlaybackContentType.STARTOVER_RESUME:
            case PlaybackContentType.CATCHUP_BEGINNING:
            case PlaybackContentType.CATCHUP_RESUME:
                this.startSoCuPlayback(playable);
                break;
            case PlaybackContentType.NDVR_LIVE:
            case PlaybackContentType.NDVR_RECORDED:
                this.startNdvrPlayback(playable);

                break;
            default:
        }
    }

    /**
     *This Function tells if we can show the delete recording overlay or not.
     * Will return true if 85% recording is watched and recording is < 30 minutes.
     * will return true if 5 mintues of recording is left.
     * every calculation is done in seconds
     * @returns boolean
     */
    public canShowDeleteRecordingOverlay = (): boolean => {
        if (!this.isNdvr()) return false;

        // Define the 5-minute threshold in seconds
        const fiveMinutesInSeconds = 5 * 60;

        // Define the 30-minutes seek duration in seconds
        const thirtyMinutesInSeconds = 30 * 60;

        const { offerPreview } = this.getMetadata();

        let currentTime = this.getPlayerCurrentTime();

        const programStartTime = 0;

        const seekDuration =
            offerPreview?.contentPreview?.recordingDuration ??
            offerPreview?.contentPreview?.duration ??
            0;

        const start = this.isNdvrLive() ? this.getSeekRange().start : 0;
        currentTime = (currentTime - start) * MILLISECONDS_IN_A_SECOND;

        const minutesTotal = currentTime / (60 * 1000); // Total minutes from milliseconds
        const hours = Math.floor(minutesTotal / 60); // Extract hours
        const minutes = Math.floor(minutesTotal % 60); // Extract remaining minutes
        const seconds = Math.floor((minutesTotal * 60) % 60); // Extract remaining seconds

        // Calculate currentTime in total seconds
        if (hours > 0) {
            currentTime = hours * 60 * 60 + minutes * 60 + seconds;
        } else {
            currentTime = minutes * 60 + seconds;
        }

        // Calculate the 85% threshold of the total duration
        const eightyFivePercentOfDuration = seekDuration * 0.85 + programStartTime;

        // Check if recording duration is less than 30 minutes and 85% is watched
        if (
            seekDuration <= thirtyMinutesInSeconds &&
            currentTime >= eightyFivePercentOfDuration &&
            seekDuration > 0
        ) {
            return true;
        }

        const isAlmostEnded = seekDuration - fiveMinutesInSeconds;

        // Check if recording is more than 30 Minutes and only 5 minutes is left
        if (currentTime >= isAlmostEnded && seekDuration > thirtyMinutesInSeconds) {
            return true;
        }

        return false;
    };

    public updateLastViewedPosition(
        screenType: string = APPLICATION_FEATURE_AREA.PLAYER,
        forceUpdate: boolean = false,
    ): void {
        if (!this.mIsPlayerLoaded && !forceUpdate) return;
        const currentTime = this.getPlayerCurrentTime();
        if (this.isSocu()) {
            this.updateSocuLastViewedPosition(currentTime, screenType);
        }
        if (this.isNdvr()) {
            this.updateNdvrLastViewedPosition();
        }
    }

    private clearProgramBoundaryTimeout() {
        if (this.mProgramBoundaryTimeoutId) {
            clearTimeout(this.mProgramBoundaryTimeoutId);
            this.mProgramBoundaryTimeoutId = null;
        }
    }

    /**
     * Determines whether the playback restart for Startover/Catchup (SoCu) content should be
     * skipped.
     *
     * This method checks if the current playable and the new playable both belong to either
     * the CATCHUP or STARTOVER resume types, are from the same channel, and playback is ongoing.
     * If all these conditions are met, it returns `true` to indicate that the playback restart
     * should be skipped.
     *
     * @param playable - The new playable item to be checked against the current playable.
     * @returns `true` if the playback restart should be skipped; otherwise, `false`.
     */
    private shouldSkipSoCuPlaybackRestart(playable: IPlayable): boolean {
        return (
            this.isSocu() &&
            (playable.playbackContentType === PlaybackContentType.CATCHUP_RESUME ||
                playable.playbackContentType === PlaybackContentType.STARTOVER_RESUME) &&
            JSON.stringify(this.mPlayable?.channel) === JSON.stringify(playable.channel)
        );
    }

    // eslint-disable-next-line class-methods-use-this
    private getEndTimeOfCurrentProgram(
        airingOffers?: AiringOffer[],
        offerPreview?: TOfferPreviewCustomData,
    ) {
        const duration = airingOffers?.[0]?.duration || offerPreview?.contentPreview?.duration || 0;
        const startTime =
            airingOffers?.[0]?.startTime || offerPreview?.contentPreview?.startTime || '';

        if (duration !== 0 && startTime !== '') {
            return new DateTime(startTime).addSeconds(duration);
        }

        if (offerPreview?.contentPreview?.endTime) {
            return new Date(offerPreview.contentPreview.endTime);
        }

        return new Date();
    }

    private onProgramBoundaryHandler(programEndTime: number) {
        const updatedProgramCurrentTime =
            this.getPlayerCurrentTime() * Constants.MILLISECOND_IN_ONE_SECOND;
        if (updatedProgramCurrentTime < programEndTime) {
            this.scheduleProgramBoundary();
        } else {
            this.prepareMetadata(this.mPlayable!);
            this.mProgramBoundarySubscription.notify();
        }
    }

    private scheduleProgramBoundary() {
        this.clearProgramBoundaryTimeout();
        const metadata = this.mMetadataManager.getMetadata();
        const { airingOffers, offerPreview } = metadata;

        if (!airingOffers || airingOffers.length === 0) {
            return;
        }

        const programCurrentTime =
            this.getPlayerCurrentTime() * Constants.MILLISECOND_IN_ONE_SECOND;
        const programEndTime = this.getEndTimeOfCurrentProgram(
            airingOffers,
            offerPreview,
        ).getTime();
        const programBoundaryTriggerTime = programEndTime - programCurrentTime;
        const onProgramBoundaryHandler = this.onProgramBoundaryHandler.bind(this);
        this.mProgramBoundaryTimeoutId = setTimeout(
            onProgramBoundaryHandler,
            programBoundaryTriggerTime,
            programEndTime,
        );
    }

    private setLastViewedPositionInterval(): void {
        this.clearLastViewedPositionInterval();
        this.mLVPIntervalId = setInterval(() => {
            if (!this.isLive()) {
                this.updateLastViewedPosition();
            }
        }, LAST_VIEWED_POSITION_INTERVAL_TIME);
    }

    private setPlayable(playable: IPlayable) {
        if (
            playable?.playbackContentType === PlaybackContentType.LIVE &&
            this.mPlayable?.channel?.channelNumber === playable.channel?.channelNumber &&
            this.mPlayable?.playbackContentType === playable?.playbackContentType
        ) {
            return;
        }
        this.mPlayable = playable;
        this.mPlayableChangeSubscription.notify(playable);
    }

    // ==== Antiburn Control ====
    public setIsAntiburnActive(isAntiburnActive: boolean): void {
        this.mIsAntiburnActive = isAntiburnActive;
    }
}
